/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import cn from 'classnames'
import { head } from 'lodash'

import CartItems from '../components/CartItems'
import styles from './CartSidebar.module.scss'
import networkConnected from '../utils/networkConnected'
import useOfflineModal from '../hooks/useOfflineModal'

import { useDispatch, useSelector } from 'react-redux'
import { getCurrentPaymentFlow, getPaymentProgress } from '../VNCustomerFacingDisplay/Selectors'
import { addDonationsToOrderInProgress, addTipOrderInProgress } from '../actions/order'
import { CFD_SCREEN } from '../VNCustomerFacingDisplay/Reducer'
import useCreateOrderInProgress from '../hooks/useCreateOrderInProgress'
import { sendCFDScreenNav } from '../VNAndroidSDK/bridgeCalls/VNWebSDKDataSend'
import { CFD_POS_PAYMENT_FLOWS } from '../VNCustomerFacingDisplay/Enums'
import { setOpenCustomerPaymentDialog } from '../VNDialogs/ActionCreators'
import { getCustomerPaymentDialogOpen } from '../VNDialogs/Selectors'
import { getOrderInProgress } from '../selectors/order'
import { getCurrentMenu, getStands } from '../selectors/menus'
import useGetIsMobileScreen from '../hooks/useGetIsMobileScreen'
import { getIsKeyboardActive } from '../VNAndroidSDK/Selectors'
import { isTabbed } from '../utils/orderStates'
import { nextPosOrderNumber } from '../utils/orderNumbers'
import VNAllOrdersButton from '../VNCart/components/VNAllOrdersButton'
import VNPaymentButtons from '../VNCart/components/VNPaymentButtons'
import VNTabButtons from '../VNCart/components/VNTabButtons'
import { menusSucceeded } from '../actions/menus'
import { isFeatureOn } from '../VNConfigurations/Selectors'
import { VNCONFIGS_FEATURE_ENABLEMENT_KEYS } from '../VNConfigurations/Enums'

const CartSidebar = ({
  order,
  history,
  className,
  remoteCartTotals,
  remoteOrderTotalLoading,
  setIsRichCheckout,
  setGetCartTotals,
  loadRemoteOrderTotal,
  remoteOrderTotal,
  remoteOrderTotalCompleted,
}) => {
  const classNames = cn(styles.cartSidebar, className)
  const cardReaderData = history?.location?.state?.cardReaderData ?? null

  const [checkIfOfflineWithCallback, offlineModal] = useOfflineModal()
  const [createOrder, orderCreated] = useCreateOrderInProgress()
  const isKeyboardActive = useSelector((state) => getIsKeyboardActive(state))
  const orderInProgress = useSelector((state) => getOrderInProgress(state))
  const paymentProgress = useSelector((state) => getPaymentProgress(state))
  const qrPayDialog = useSelector((state) => getCustomerPaymentDialogOpen(state))
  const isMobileViewPort = useGetIsMobileScreen()
  const currentPaymentFlow = useSelector((state) => getCurrentPaymentFlow(state))
  const stands = useSelector(getStands)
  const menu = useSelector(getCurrentMenu)
  const disablePPISplitOffline = useSelector((state) =>
    isFeatureOn(state, VNCONFIGS_FEATURE_ENABLEMENT_KEYS.DISABLE_PPI_SPLIT_OFFLINE),
  )
  const confirmationNumber = nextPosOrderNumber()

  const dispatch = useDispatch()

  const offlineModalScanScreenMessage = isMobileViewPort
    ? 'This payment tender is not supported while the device is offline'
    : 'This payment tender is not supported\nwhile the device is offline'

  const offlineGiftCardModalMessage = isMobileViewPort
    ? 'Gift card tender is not supported while the device is offline'
    : 'Gift card tender is not supported while\n the device is offline'

  useEffect(() => {
    if (paymentProgress?.tipAmount && qrPayDialog?.get('open')) {
      createOrder()
    }
  }, [paymentProgress, qrPayDialog])

  useEffect(() => {
    const tipAmount = parseInt(paymentProgress?.tipAmount)
    if (!paymentProgress.tipAmount || !qrPayDialog?.get('open') || !orderCreated) return

    if (paymentProgress?.charityAmountInCents) {
      dispatch(addDonationsToOrderInProgress(parseInt(paymentProgress?.charityAmountInCents)))
    }

    dispatch(setOpenCustomerPaymentDialog({ open: false }))
    dispatch(addTipOrderInProgress(tipAmount))
    if (currentPaymentFlow === CFD_POS_PAYMENT_FLOWS.TICKET) {
      checkIfOfflineWithCallback(() => history.push('/ticket'), offlineModalScanScreenMessage, disablePPISplitOffline)
    } else if (currentPaymentFlow === CFD_POS_PAYMENT_FLOWS.QR_PAY) {
      const tipAmount = parseInt(paymentProgress?.tipAmount)
      const charityAmount = parseInt(paymentProgress?.charityAmountInCents)
      checkIfOfflineWithCallback(
        () =>
          history.push({
            pathname: '/qr-pay',
            state: {
              tip: tipAmount,
              charityAmount: charityAmount,
            },
          }),
        offlineModalScanScreenMessage,
        disablePPISplitOffline,
      )
      sendCFDScreenNav(CFD_SCREEN.PRESENT_QR_PAY_CODE, {
        cartTotals: {
          ...remoteCartTotals,
          uuid: orderInProgress.uuid,
          remainingTicketBalance: orderInProgress?.remainingTicketBalance,
        },
      })
    } else if (currentPaymentFlow === CFD_POS_PAYMENT_FLOWS.GIFT_CARD) {
      checkIfOfflineWithCallback(() => history.push('/gift-card'), offlineGiftCardModalMessage, disablePPISplitOffline)
    }
  }, [paymentProgress, qrPayDialog, orderCreated])

  useEffect(() => {
    // if offline
    networkConnected().catch(() => {
      if (menu) {
        dispatch(menusSucceeded({ data: { stands: [head(stands)], menus: [menu] } }))
      }
    })
  }, [menu])

  const headerText = isTabbed(orderInProgress) ? orderInProgress.tabName : '#' + confirmationNumber

  return (
    <div className={classNames}>
      <div className={styles.header}>
        <span className={styles.cartName}>{headerText}</span>
        <VNAllOrdersButton className={styles.allOrdersButton} />
      </div>

      <div className={styles.border}></div>
      <CartItems className={styles.cartItems} remoteOrderTotalLoading={remoteOrderTotalLoading} />
      <div className={cn(styles.cartDetailsContainer, { [styles.hidden]: isKeyboardActive })}>
        <VNPaymentButtons
          loadRemoteOrderTotal={loadRemoteOrderTotal}
          setIsRichCheckout={setIsRichCheckout}
          setGetCartTotals={setGetCartTotals}
          remoteOrderTotalCompleted={remoteOrderTotalCompleted}
          remoteOrderTotal={remoteOrderTotal}
        />
        <VNTabButtons cardReaderData={cardReaderData} />
      </div>
      {offlineModal}
    </div>
  )
}

CartSidebar.defaultProps = {}

CartSidebar.propTypes = {
  className: PropTypes.string,
}

export default CartSidebar
